import { Pencil, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/shadcn/button'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/shadcn/table'
import { SortableTableHead } from '@/components/common/SortableTableHead'
import { LoaderTable } from '@/components/common/loaders/LoaderTable'
import type { ExtendedGroupResponse, OrderType } from '@/api/model'
import { ActionModuleIcon } from '../components/ActionModuleIcon'
import {ShopModuleIcon, shopModuleLabel} from '../components/ShopModuleIcon'
import {ServiceModuleIcon} from "@/pages/groups/components/ServiceModuleIcon.tsx";
import {MAX_VISIBLE, ModuleIconList} from "@/pages/groups/components/ModuleIconList.tsx";

export const GroupSortFields = {
  Name: 'Name',
} as const

interface Props {
  isLoading: boolean
  isError: boolean
  groups: ExtendedGroupResponse[]
  currentSort?: string
  currentOrder?: OrderType
  onSort: (sort?: string, order?: OrderType) => void
  onEdit: (group: ExtendedGroupResponse) => void
  onDelete: (group: ExtendedGroupResponse) => void
  canWrite: boolean
}

export function GroupsDataTable({ isLoading, isError, groups, currentSort, currentOrder, onSort, onEdit, onDelete, canWrite }: Props) {
  const { t } = useTranslation('common')
  return (
    <Table>
      <TableHeader className="sticky top-0 z-10 bg-card shadow-sm [&_th]:bg-card">
        <TableRow>
          <SortableTableHead field={GroupSortFields.Name} currentSort={currentSort} currentOrder={currentOrder} onSort={onSort}>
            {t('columns.name')}
          </SortableTableHead>
          <TableHead>{t('columns.actionModules')}</TableHead>
          <TableHead>{'Service Modules'}</TableHead>
          <TableHead>{t('columns.shopModules')}</TableHead>
          <TableHead>{t('columns.members')}</TableHead>
          <TableHead className="w-20" />
        </TableRow>
      </TableHeader>
      <TableBody>
        <LoaderTable isLoading={isLoading} isError={isError} isEmpty={groups.length === 0} colSpan={5}>
          {groups.map((g) => (
          <TableRow key={g.id}>
            <TableCell className="font-medium">{g.name ?? '—'}</TableCell>
              <TableCell>
                  <ModuleIconList restLabels={(g.actionModules ?? []).slice(MAX_VISIBLE).map(a => a.name ?? '—')}>
                      {(g.actionModules ?? []).map((a) => <ActionModuleIcon key={a.id} module={a} />)}
                  </ModuleIconList>
              </TableCell>
              <TableCell>
                  <ModuleIconList restLabels={(g.serviceModules ?? []).slice(MAX_VISIBLE).map(s => s.name ?? '—')}>
                      {(g.serviceModules ?? []).map((s) => <ServiceModuleIcon key={s.id} module={s} />)}
                  </ModuleIconList>
              </TableCell>
              <TableCell>
                  <ModuleIconList restLabels={(g.shopModules ?? []).slice(MAX_VISIBLE).map(s => shopModuleLabel(s.type))}>
                      {(g.shopModules ?? []).map((s) => <ShopModuleIcon key={s.id} type={s.type} />)}
                  </ModuleIconList>
              </TableCell>
            <TableCell>
              <span className="text-sm">{g.memberCount ?? 0}</span>
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" onClick={() => onEdit(g)} disabled={!canWrite}>
                  <Pencil className="size-4" />
                </Button>
                {canWrite && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:text-destructive"
                    onClick={() => onDelete(g)}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                )}
              </div>
            </TableCell>
          </TableRow>
          ))}
        </LoaderTable>
      </TableBody>
    </Table>
  )
}
